'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'

const STORAGE_KEY = 'smoking-austria-animations-enabled'

interface AnimationContextType {
    animationsEnabled: boolean
    toggleAnimations: () => void
}

const AnimationContext = createContext<AnimationContextType>({
    animationsEnabled: true,
    toggleAnimations: () => { },
})

export const AnimationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [animationsEnabled, setAnimationsEnabled] = useState(true)

    // Read from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY)
            if (stored !== null) {
                setAnimationsEnabled(stored === 'true')
            } else {
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
                const isMobile = window.innerWidth < 768
                
                if (prefersReducedMotion || isMobile) {
                    setAnimationsEnabled(false)
                }
            }
        } catch {
            // localStorage not available (SSR or private mode)
        }
    }, [])

    // Double-tap (double-click) anywhere toggles the flame / charge animation.
    // Lives in the provider (always mounted) so the gesture works even while the
    // canvas is hidden. Ignores interactive/editable controls (so links, buttons
    // and form fields behave normally); on everything else it toggles and clears
    // the word the double-click happened to select, so no highlight lingers.
    useEffect(() => {
        const onDblClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null
            if (
                target?.closest(
                    'a, button, input, textarea, select, label, [contenteditable], [role="button"]',
                )
            ) {
                return
            }
            window.getSelection?.()?.removeAllRanges()
            setAnimationsEnabled((prev) => {
                const next = !prev
                try {
                    localStorage.setItem(STORAGE_KEY, String(next))
                } catch {
                    // ignore
                }
                return next
            })
        }
        window.addEventListener('dblclick', onDblClick)
        return () => window.removeEventListener('dblclick', onDblClick)
    }, [])

    const toggleAnimations = () => {
        setAnimationsEnabled((prev) => {
            const next = !prev
            try {
                localStorage.setItem(STORAGE_KEY, String(next))
            } catch {
                // ignore
            }
            return next
        })
    }

    return (
        <AnimationContext.Provider value={{ animationsEnabled, toggleAnimations }}>
            {children}
        </AnimationContext.Provider>
    )
}

export const useAnimation = () => useContext(AnimationContext)
